import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, functions } from "../firebase";
import { perfMark } from "../utils/perf";
import Icon from "../components/Icon";

// ── ไอคอนโลโก้ Google (SVG ทางการ) ──────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12
        c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24
        c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039
        l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36
        c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571
        c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24
        C44,22.659,43.862,21.35,43.611,20.083z"/>
    </svg>
  );
}

// ── ดึงจังหวัดจาก IP แล้วบันทึกลง Firestore (ครั้งเดียวตอน login/สมัคร) ──
async function saveProvinceIfMissing(uid) {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (snap.exists() && snap.data().province) return; // มีแล้ว ไม่ต้องดึงซ้ำ
    const res  = await fetch("https://ipwho.is/");
    const geo  = await res.json();
    if (geo.success && geo.region) {
      await updateDoc(doc(db, "users", uid), { province: geo.region });
    }
  } catch (_) { /* ไม่ทำอะไรถ้า API ล่ม */ }
}

const css = `
  .login-page { min-height:100vh;background:#f5f6f8;display:flex;align-items:center;justify-content:center;font-family:'Prompt',sans-serif;padding:20px;position:relative;overflow:hidden; }
  .login-bg { position:absolute;inset:0;background:radial-gradient(ellipse 60% 50% at 50% 0%,rgba(247,71,53,0.08) 0%,transparent 70%);pointer-events:none; }
  .grid-bg { position:absolute;inset:0;background-image:linear-gradient(rgba(247,71,53,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(247,71,53,0.03) 1px,transparent 1px);background-size:60px 60px;pointer-events:none; }
  .login-card { background:#ffffff;border:1px solid rgba(247,71,53,0.15);border-radius:24px;padding:48px 40px;width:100%;max-width:420px;position:relative;z-index:1;box-shadow:0 40px 80px rgba(0,0,0,0.4); }
  .login-logo { text-align:center;margin-bottom:32px; }
  .login-logo .logo-img { height:80px;width:auto;mix-blend-mode:screen;filter:brightness(1.1); }
  .login-logo .logo span { color:#242b36; }
  .login-logo p { color:#88919e;font-size:0.82rem;margin-top:6px; }
  .login-tabs { display:flex;background:#f5f6f8;border-radius:12px;padding:4px;margin-bottom:28px;border:1px solid rgba(247,71,53,0.1); }
  .login-tab { flex:1;padding:10px;border:none;background:transparent;color:#88919e;font-family:'Prompt',sans-serif;font-size:0.85rem;cursor:pointer;border-radius:9px;transition:all 0.2s; }
  .login-tab.active { background:#dc3728;color:#f5f6f8;font-weight:600; }
  .form-group { margin-bottom:16px; }
  .form-label { display:block;font-size:0.78rem;color:#88919e;margin-bottom:6px; }
  .form-input { width:100%;padding:13px 16px;background:#f5f6f8;border:1px solid rgba(247,71,53,0.15);border-radius:12px;color:#242b36;font-family:'Prompt',sans-serif;font-size:0.88rem;outline:none;transition:border-color 0.2s;box-sizing:border-box; }
  .form-input:focus { border-color:rgba(247,71,53,0.5); }
  .form-input::placeholder { color:#a0a7b3; }
  .otp-input { letter-spacing:10px;font-size:1.4rem;font-weight:700;text-align:center; }
  .btn-login { width:100%;padding:14px;background:#dc3728;border:none;border-radius:12px;color:#f5f6f8;font-family:'Prompt',sans-serif;font-size:0.95rem;font-weight:700;cursor:pointer;margin-top:8px;transition:all 0.2s; }
  .btn-login:hover { background:#e63626;transform:translateY(-1px); }
  .btn-login:disabled { opacity:0.6;cursor:not-allowed;transform:none; }
  .btn-outline { width:100%;padding:12px;background:transparent;border:1px solid rgba(247,71,53,0.3);border-radius:12px;color:#dc3728;font-family:'Prompt',sans-serif;font-size:0.88rem;font-weight:600;cursor:pointer;margin-top:8px;transition:all 0.2s; }
  .btn-outline:hover { background:rgba(247,71,53,0.08); }
  .btn-outline:disabled { opacity:0.5;cursor:not-allowed; }
  .divider { display:flex;align-items:center;gap:12px;margin:20px 0;color:#a0a7b3;font-size:0.75rem; }
  .divider::before,.divider::after { content:'';flex:1;height:1px;background:rgba(247,71,53,0.1); }
  .social-btns { display:flex;gap:10px; }
  .btn-social { flex:1;padding:11px;background:transparent;border:1px solid rgba(247,71,53,0.15);border-radius:12px;color:#88919e;font-family:'Prompt',sans-serif;font-size:0.82rem;cursor:pointer;transition:all 0.2s;display:flex;align-items:center;justify-content:center;gap:8px; }
  .btn-social:hover { border-color:rgba(247,71,53,0.4);color:#242b36; }
  .error-msg { background:rgba(255,85,85,0.1);border:1px solid rgba(255,85,85,0.3);border-radius:10px;padding:10px 14px;color:#ff7777;font-size:0.8rem;margin-bottom:16px;text-align:center; }
  .success-msg { background:rgba(247,71,53,0.1);border:1px solid rgba(247,71,53,0.3);border-radius:10px;padding:10px 14px;color:#dc3728;font-size:0.8rem;margin-bottom:16px;text-align:center; }
  .input-wrap { position:relative; }
  .eye-btn { position:absolute;right:14px;top:50%;transform:translateY(-50%);background:none;border:none;color:#88919e;cursor:pointer;font-size:1rem;padding:0; }
  .form-select { width:100%;padding:13px 16px;background:#f5f6f8;border:1px solid rgba(247,71,53,0.15);border-radius:12px;color:#242b36;font-family:'Prompt',sans-serif;font-size:0.88rem;outline:none;transition:border-color 0.2s;box-sizing:border-box;appearance:none;cursor:pointer; }
  .form-select:focus { border-color:rgba(247,71,53,0.5); }
  .form-select option { background:#ffffff; }
  .strength-bar { display:flex;gap:4px;margin-top:6px; }
  .strength-seg { flex:1;height:3px;border-radius:2px;background:#eef0f3;transition:background 0.3s; }
  .otp-info { background:rgba(247,71,53,0.06);border:1px solid rgba(247,71,53,0.2);border-radius:10px;padding:12px 14px;font-size:0.8rem;color:#88919e;margin-bottom:16px;text-align:center;line-height:1.6; }
  .step-indicator { display:flex;align-items:center;gap:8px;margin-bottom:20px; }
  .step-dot { width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:0.75rem;font-weight:700;flex-shrink:0; }
  .step-dot.active { background:#dc3728;color:#f5f6f8; }
  .step-dot.done { background:#1a3a25;color:#dc3728;border:1px solid rgba(247,71,53,0.4); }
  .step-dot.inactive { background:#eef0f3;color:#a0a7b3; }
  .step-line { flex:1;height:1px;background:rgba(247,71,53,0.15); }
  @media(max-width:480px){
    .login-page{padding:12px;}
    .login-card{padding:32px 22px;}
  }
  @media(max-width:360px){
    .login-card{padding:24px 16px;}
    .login-logo .logo-img{height:34px;}
    .social-btns{flex-direction:column;}
    .btn-social{padding:10px;}
  }
`;

const STRENGTH_COLORS = ["#ff5555", "#c77b27", "#c77b27", "#dc3728"];
const STRENGTH_LABELS = ["", "อ่อน", "พอใช้", "ดี", "แข็งแรง"];

const THAI_PROVINCES = [
  "กรุงเทพมหานคร","กระบี่","กาญจนบุรี","กาฬสินธุ์","กำแพงเพชร","ขอนแก่น","จันทบุรี","ฉะเชิงเทรา",
  "ชลบุรี","ชัยนาท","ชัยภูมิ","ชุมพร","เชียงราย","เชียงใหม่","ตรัง","ตราด","ตาก","นครนายก",
  "นครปฐม","นครพนม","นครราชสีมา","นครศรีธรรมราช","นครสวรรค์","นนทบุรี","นราธิวาส","น่าน",
  "บึงกาฬ","บุรีรัมย์","ปทุมธานี","ประจวบคีรีขันธ์","ปราจีนบุรี","ปัตตานี","พระนครศรีอยุธยา",
  "พะเยา","พังงา","พัทลุง","พิจิตร","พิษณุโลก","เพชรบุรี","เพชรบูรณ์","แพร่","ภูเก็ต",
  "มหาสารคาม","มุกดาหาร","แม่ฮ่องสอน","ยโสธร","ยะลา","ร้อยเอ็ด","ระนอง","ระยอง","ราชบุรี",
  "ลพบุรี","ลำปาง","ลำพูน","เลย","ศรีสะเกษ","สกลนคร","สงขลา","สตูล","สมุทรปราการ",
  "สมุทรสงคราม","สมุทรสาคร","สระแก้ว","สระบุรี","สิงห์บุรี","สุโขทัย","สุพรรณบุรี",
  "สุราษฎร์ธานี","สุรินทร์","หนองคาย","หนองบัวลำภู","อ่างทอง","อำนาจเจริญ","อุดรธานี",
  "อุตรดิตถ์","อุทัยธานี","อุบลราชธานี",
];

function getStrength(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { state: navigationState } = useLocation();
  const destination = navigationState?.from?.startsWith("/") && !navigationState.from.startsWith("//") ? navigationState.from : "/home";
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPass] = useState("");
  const [name, setName] = useState("");
  const [regProvince, setRegProvince] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ── Social: popup เลือกจังหวัดสำหรับสมาชิกใหม่ ─────────────────────────
  const [showProvinceModal, setShowProvinceModal] = useState(false);
  const [socialProvince, setSocialProvince] = useState("");
  const [pendingSocialUid, setPendingSocialUid] = useState(null);
  const [socialProvinceLoading, setSocialProvinceLoading] = useState(false);
  const strength = getStrength(password);
  const clear = () => {
    setError("");
    setSuccess("");
  };

  // ── OTP reset flow ────────────────────────────────────────────────────────
  const [resetMode, setResetMode] = useState(false); // false | 'otp'
  const [otpStep, setOtpStep] = useState(1); // 1 = send OTP, 2 = verify OTP
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmNewPw, setConfirmNewPw] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const newPwStrength = getStrength(newPw);

  const startOtpCooldown = (secs = 60) => {
    setOtpCooldown(secs);
    const t = setInterval(() => {
      setOtpCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(t);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const openResetMode = () => {
    setResetMode("otp");
    setOtpStep(1);
    setOtpEmail(email); // prefill with whatever email user typed
    setOtpCode("");
    setNewPw("");
    setConfirmNewPw("");
    setOtpCooldown(0);
    clear();
  };

  const closeResetMode = () => {
    setResetMode(false);
    setOtpStep(1);
    setOtpCode("");
    setNewPw("");
    setConfirmNewPw("");
    clear();
  };

  // Step 1: Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    clear();
    if (!otpEmail) {
      setError("กรุณากรอกอีเมล");
      return;
    }
    if (otpCooldown > 0) return;
    setLoading(true);
    try {
      const sendOtp = httpsCallable(functions, "sendOtp");
      await sendOtp({ email: otpEmail });
      setOtpStep(2);
      setSuccess(
        `ส่ง OTP ไปที่ ${otpEmail} แล้ว — กรุณาตรวจสอบกล่องจดหมาย (และโฟลเดอร์ Spam)`,
      );
      startOtpCooldown(60);
    } catch (err) {
      const msg = err?.message || "";
      if (msg.includes("not-found") || msg.includes("ไม่พบ"))
        setError("ไม่พบอีเมลนี้ในระบบ");
      else setError("ส่ง OTP ไม่สำเร็จ กรุณาลองใหม่");
    }
    setLoading(false);
  };

  // Step 2: Verify OTP and change password
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    clear();
    if (!otpCode || otpCode.length < 6) {
      setError("กรุณากรอก OTP 6 หลัก");
      return;
    }
    if (!newPw || newPw.length < 8) {
      setError("รหัสผ่านใหม่ต้องมีอย่างน้อย 8 ตัวอักษร");
      return;
    }
    if (newPw !== confirmNewPw) {
      setError("รหัสผ่านใหม่และยืนยันไม่ตรงกัน");
      return;
    }
    setLoading(true);
    try {
      const verifyOtp = httpsCallable(functions, "verifyOtpAndResetPassword");
      await verifyOtp({ email: otpEmail, otp: otpCode, newPassword: newPw });
      setSuccess("เปลี่ยนรหัสผ่านสำเร็จ! กรุณาเข้าสู่ระบบด้วยรหัสผ่านใหม่");
      setTimeout(() => {
        closeResetMode();
      }, 2500);
    } catch (err) {
      const msg = err?.message || "";
      if (msg.includes("unauthenticated") || msg.includes("OTP ไม่ถูกต้อง"))
        setError("OTP ไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง");
      else if (msg.includes("deadline-exceeded") || msg.includes("หมดอายุ"))
        setError("OTP หมดอายุแล้ว กรุณาขอ OTP ใหม่");
      else if (msg.includes("not-found") || msg.includes("ไม่พบ OTP"))
        setError("ไม่พบ OTP กรุณาขอ OTP ใหม่");
      else setError("เปลี่ยนรหัสผ่านไม่สำเร็จ: " + msg);
    }
    setLoading(false);
  };

  // Re-send OTP (back to step 1 but keep email)
  const handleResendOtp = async () => {
    if (otpCooldown > 0) return;
    clear();
    setOtpCode("");
    setLoading(true);
    try {
      const sendOtp = httpsCallable(functions, "sendOtp");
      await sendOtp({ email: otpEmail });
      setSuccess(`ส่ง OTP ใหม่ไปที่ ${otpEmail} แล้ว`);
      startOtpCooldown(60);
    } catch (err) {
      setError("ส่ง OTP ไม่สำเร็จ กรุณาลองใหม่");
    }
    setLoading(false);
  };

  // ── Login ────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    clear();
    if (!email || !password) {
      setError("กรุณากรอกข้อมูลให้ครบ");
      return;
    }
    setLoading(true);
    const doneLogin = perfMark("LoginPage: signInWithEmailAndPassword");
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      doneLogin("สำเร็จ");
      const profile = await getDoc(doc(db, "users", result.user.uid)).catch(() => null);
      localStorage.setItem(
        "klang-user",
        JSON.stringify({ uid: result.user.uid, email: result.user.email, name: profile?.data()?.name || result.user.displayName || result.user.email?.split("@")[0] }),
      );
      saveProvinceIfMissing(result.user.uid);
      setSuccess("เข้าสู่ระบบสำเร็จ!");
      setTimeout(() => navigate(destination), 1000);
    } catch (err) {
      doneLogin("error: " + err.code);
      setError("อีเมลหรือรหัสผ่านไม่ถูกต้อง");
    }
    setLoading(false);
  };

  // ── Register ─────────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    clear();
    if (!name || !email || !password || !regProvince) {
      setError("กรุณากรอกข้อมูลให้ครบและเลือกจังหวัด");
      return;
    }
    if (password.length < 8) {
      setError("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร");
      return;
    }
    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      await setDoc(doc(db, "users", result.user.uid), {
        name,
        email,
        province: regProvince,
        createdAt: new Date(),
        role: "user",
      });
      localStorage.setItem(
        "klang-user",
        JSON.stringify({ uid: result.user.uid, email, name }),
      );
      saveProvinceIfMissing(result.user.uid);
      setSuccess("สมัครสมาชิกสำเร็จ!");
      setTimeout(() => navigate("/"), 1000);
    } catch (err) {
      if (err.code === "auth/email-already-in-use")
        setError("อีเมลนี้ถูกใช้แล้ว");
      else setError("สมัครไม่สำเร็จ กรุณาลองใหม่");
    }
    setLoading(false);
  };

  // ── helper: หลัง social login สำเร็จ ────────────────────────────────────
  const afterSocialLogin = async (result, providerName) => {
    const uid = result.user.uid;
    const snap = await getDoc(doc(db, "users", uid));
    const isNew = !snap.exists();

    await setDoc(doc(db, "users", uid), {
      name: result.user.displayName,
      email: result.user.email,
      photoURL: result.user.photoURL || null,
      ...(isNew ? { createdAt: serverTimestamp(), role: "user" } : {}),
    }, { merge: true });

    localStorage.setItem("klang-user", JSON.stringify({
      uid,
      email: result.user.email,
      name: result.user.displayName,
    }));

    if (isNew) {
      // สมาชิกใหม่ → ให้เลือกจังหวัดก่อน
      setPendingSocialUid(uid);
      setShowProvinceModal(true);
      setSuccess(`สมัครด้วย ${providerName} สำเร็จ! กรุณาเลือกจังหวัดของคุณ`);
    } else {
      saveProvinceIfMissing(uid);
      setSuccess(`เข้าสู่ระบบด้วย ${providerName} สำเร็จ!`);
      setTimeout(() => navigate(destination), 1000);
    }
  };

  const handleSaveProvince = async () => {
    if (!socialProvince) return;
    setSocialProvinceLoading(true);
    try {
      await setDoc(doc(db, "users", pendingSocialUid), { province: socialProvince }, { merge: true });
      setShowProvinceModal(false);
      navigate(destination);
    } catch {
      setError("บันทึกจังหวัดไม่สำเร็จ กรุณาลองใหม่");
    }
    setSocialProvinceLoading(false);
  };

  // ── Social login ─────────────────────────────────────────────────────────
  const handleGoogle = async () => {
    clear();
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await afterSocialLogin(result, "Google");
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user") setError("ปิด popup ก่อนเสร็จ");
      else if (err.code === "auth/cancelled-popup-request") { /* ignore */ }
      else setError("Google login ไม่สำเร็จ: " + (err.message || ""));
    }
    setLoading(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="login-page kl-auth">
      <style>{css}</style>
      <div className="login-bg" />
      <div className="grid-bg" />
      <button
        onClick={() => navigate("/")}
        style={{position:"absolute",top:20,left:20,zIndex:10,background:"transparent",border:"1px solid rgba(247,71,53,0.2)",borderRadius:10,color:"#88919e",padding:"7px 14px",cursor:"pointer",fontFamily:"Prompt,sans-serif",fontSize:"0.8rem",display:"flex",alignItems:"center",gap:6,transition:"all 0.2s"}}
        onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(247,71,53,0.5)";e.currentTarget.style.color="#dc3728"}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(247,71,53,0.2)";e.currentTarget.style.color="#88919e"}}
      >← กลับ</button>
      <aside className="kl-auth-brand"><a className="kl-wordmark" href="/">klang.</a><span className="kl-auth-check"><Icon name="check" size={76} /></span><h1>ซื้อขายมั่นใจ<br />ให้ Klang ดูแล</h1><p>วางเงิน • ส่งมอบ • ยืนยันรับสินค้า</p></aside>
      <div className="login-card">
        <div className="login-logo">
          <span className="kl-wordmark">klang.</span>
          <p>ซื้อขายปลอดภัย มีเราเป็นตัวกลาง</p>
        </div>

        {/* ── OTP Reset Mode ── */}
        {resetMode === "otp" ? (
          <>
            {/* Step indicator */}
            <div className="step-indicator">
              <div className={`step-dot ${otpStep === 1 ? "active" : "done"}`}>
                {otpStep > 1 ? "✓" : "1"}
              </div>
              <div className="step-line" />
              <div
                className={`step-dot ${otpStep === 2 ? "active" : "inactive"}`}
              >
                2
              </div>
            </div>

            <div
              style={{ fontSize: "0.9rem", fontWeight: 600, marginBottom: 4 }}
            >
              {otpStep === 1 ? "ลืมรหัสผ่าน" : "ยืนยัน OTP"}
            </div>
            <div
              style={{
                fontSize: "0.78rem",
                color: "#88919e",
                marginBottom: 16,
              }}
            >
              {otpStep === 1
                ? "กรอกอีเมลที่ใช้สมัคร ระบบจะส่งรหัส OTP ให้"
                : "กรอกรหัส OTP 6 หลักที่ส่งไปที่อีเมลของคุณ"}
            </div>

            {error && <div className="error-msg">{error}</div>}
            {success && <div className="success-msg">{success}</div>}

            {/* Step 1: Enter email */}
            {otpStep === 1 && (
              <form onSubmit={handleSendOtp}>
                <div className="form-group">
                  <label className="form-label">อีเมลที่ใช้สมัคร</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="your@email.com"
                    value={otpEmail}
                    aria-label="อีเมลที่ใช้สมัคร"
                    autoComplete="email"
                    onChange={(e) => setOtpEmail(e.target.value)}
                  />
                </div>
                <button
                  className="btn-login"
                  type="submit"
                  disabled={loading || !otpEmail}
                >
                  {loading ? "กำลังส่ง OTP..." : "ส่ง OTP →"}
                </button>
              </form>
            )}

            {/* Step 2: Enter OTP + new password */}
            {otpStep === 2 && (
              <form onSubmit={handleVerifyOtp}>
                <div className="otp-info">
                  ส่ง OTP ไปที่{" "}
                  <strong style={{ color: "#242b36" }}>{otpEmail}</strong>
                  <br />
                  รหัส OTP มีอายุ{" "}
                  <strong style={{ color: "#c77b27" }}>10 นาที</strong>
                </div>

                <div className="form-group">
                  <label className="form-label">รหัส OTP 6 หลัก</label>
                  <input
                    className="form-input otp-input"
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    maxLength={6}
                    value={otpCode}
                    aria-label="รหัส OTP 6 หลัก"
                    autoComplete="one-time-code"
                    onChange={(e) =>
                      setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">รหัสผ่านใหม่</label>
                  <div className="input-wrap">
                    <input
                      className="form-input"
                      type={showNewPw ? "text" : "password"}
                      placeholder="อย่างน้อย 8 ตัวอักษร"
                      value={newPw}
                      aria-label="รหัสผ่านใหม่"
                      autoComplete="new-password"
                      onChange={(e) => setNewPw(e.target.value)}
                      style={{ paddingRight: 44 }}
                    />
                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowNewPw(!showNewPw)}
                      aria-label={showNewPw ? "ซ่อนรหัสผ่านใหม่" : "แสดงรหัสผ่านใหม่"}
                      aria-pressed={showNewPw}
                    >
                      {showNewPw ? <Icon name="eye-off" size={18} /> : <Icon name="eye" size={18} />}
                    </button>
                  </div>
                  {newPw && (
                    <>
                      <div className="strength-bar">
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            className="strength-seg"
                            style={{
                              background:
                                i <= newPwStrength
                                  ? STRENGTH_COLORS[newPwStrength - 1]
                                  : "#eef0f3",
                            }}
                          />
                        ))}
                      </div>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color:
                            STRENGTH_COLORS[newPwStrength - 1] || "#a0a7b3",
                          marginTop: 3,
                        }}
                      >
                        {STRENGTH_LABELS[newPwStrength]}
                      </div>
                    </>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">ยืนยันรหัสผ่านใหม่</label>
                  <div className="input-wrap">
                    <input
                      className="form-input"
                      type="password"
                      placeholder="พิมพ์รหัสใหม่อีกครั้ง"
                      value={confirmNewPw}
                      aria-label="ยืนยันรหัสผ่านใหม่"
                      autoComplete="new-password"
                      onChange={(e) => setConfirmNewPw(e.target.value)}
                      style={{
                        paddingRight: 44,
                        borderColor:
                          confirmNewPw && newPw !== confirmNewPw
                            ? "rgba(255,85,85,0.5)"
                            : "",
                      }}
                    />
                    {confirmNewPw && (
                      <span
                        style={{
                          position: "absolute",
                          right: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        {confirmNewPw === newPw ? <Icon name="check" size={16} style={{ color: "#dc3728" }} /> : <Icon name="x" size={16} style={{ color: "#ff5555" }} />}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  className="btn-login"
                  type="submit"
                  disabled={
                    loading ||
                    otpCode.length < 6 ||
                    !newPw ||
                    newPw !== confirmNewPw
                  }
                >
                  {loading ? "กำลังยืนยัน..." : "ยืนยันและเปลี่ยนรหัสผ่าน →"}
                </button>

                {/* Resend OTP */}
                <button
                  type="button"
                  className="btn-outline"
                  onClick={handleResendOtp}
                  disabled={loading || otpCooldown > 0}
                  style={{ marginTop: 10 }}
                >
                  {otpCooldown > 0
                    ? `ส่ง OTP ใหม่ได้ใน ${otpCooldown} วิ`
                    : "ส่ง OTP ใหม่"}
                </button>
              </form>
            )}

            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button
                onClick={closeResetMode}
                style={{
                  background: "none",
                  border: "none",
                  color: "#88919e",
                  cursor: "pointer",
                  fontSize: "0.82rem",
                }}
              >
                ← กลับหน้าเข้าสู่ระบบ
              </button>
            </div>
          </>
        ) : (
          /* ── Normal login / register ── */
          <>
            <div className="login-tabs">
              <button
                className={`login-tab${tab === "login" ? " active" : ""}`}
                onClick={() => {
                  setTab("login");
                  clear();
                }}
              >
                เข้าสู่ระบบ
              </button>
              <button
                className={`login-tab${tab === "register" ? " active" : ""}`}
                onClick={() => {
                  setTab("register");
                  clear();
                }}
              >
                สมัครสมาชิก
              </button>
            </div>
            {error && <div className="error-msg">{error}</div>}
            {success && <div className="success-msg">{success}</div>}

            {tab === "login" && (
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="form-label">อีเมล</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    aria-label="อีเมล"
                    autoComplete="username"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">รหัสผ่าน</label>
                  <div className="input-wrap">
                    <input
                      className="form-input"
                      type={showPw ? "text" : "password"}
                      placeholder="รหัสผ่าน"
                      value={password}
                      aria-label="รหัสผ่าน"
                      autoComplete="current-password"
                      onChange={(e) => setPass(e.target.value)}
                      style={{ paddingRight: 44 }}
                    />
                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowPw(!showPw)}
                      aria-label={showPw ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                      aria-pressed={showPw}
                    >
                      {showPw ? <Icon name="eye-off" size={18} /> : <Icon name="eye" size={18} />}
                    </button>
                  </div>
                </div>
                <div style={{ textAlign: "right", marginBottom: 12 }}>
                  <button
                    type="button"
                    onClick={openResetMode}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#88919e",
                      cursor: "pointer",
                      fontSize: "0.78rem",
                    }}
                  >
                    ลืมรหัสผ่าน?
                  </button>
                </div>
                <button className="btn-login" type="submit" disabled={loading}>
                  {loading ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ →"}
                </button>
                <div className="divider">หรือ</div>
                <div className="social-btns">
                  <button
                    type="button"
                    className="btn-social"
                    onClick={handleGoogle}
                  >
                    <GoogleIcon /> Google
                  </button>
                </div>
              </form>
            )}

            {tab === "register" && (
              <form onSubmit={handleRegister}>
                <div className="form-group">
                  <label className="form-label">ชื่อ-นามสกุล</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="ชื่อของคุณ"
                    value={name}
                    aria-label="ชื่อ-นามสกุล"
                    autoComplete="name"
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">อีเมล</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    aria-label="อีเมล"
                    autoComplete="email"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">รหัสผ่าน</label>
                  <div className="input-wrap">
                    <input
                      className="form-input"
                      type={showPw ? "text" : "password"}
                      placeholder="อย่างน้อย 8 ตัวอักษร"
                      value={password}
                      aria-label="รหัสผ่าน"
                      autoComplete="new-password"
                      onChange={(e) => setPass(e.target.value)}
                      style={{ paddingRight: 44 }}
                    />
                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowPw(!showPw)}
                      aria-label={showPw ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                      aria-pressed={showPw}
                    >
                      {showPw ? <Icon name="eye-off" size={18} /> : <Icon name="eye" size={18} />}
                    </button>
                  </div>
                  {password && (
                    <>
                      <div className="strength-bar">
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            className="strength-seg"
                            style={{
                              background:
                                i <= strength
                                  ? STRENGTH_COLORS[strength - 1]
                                  : "#eef0f3",
                            }}
                          />
                        ))}
                      </div>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: STRENGTH_COLORS[strength - 1] || "#a0a7b3",
                          marginTop: 4,
                        }}
                      >
                        {STRENGTH_LABELS[strength]}
                      </div>
                    </>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">จังหวัดที่อยู่ปัจจุบัน</label>
                  <select
                    className="form-select"
                    value={regProvince}
                    aria-label="จังหวัดที่อยู่ปัจจุบัน"
                    onChange={(e) => setRegProvince(e.target.value)}
                  >
                    <option value="">— เลือกจังหวัด —</option>
                    {THAI_PROVINCES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <button className="btn-login" type="submit" disabled={loading}>
                  {loading ? "กำลังสมัคร..." : "สมัครสมาชิก →"}
                </button>
                <div className="divider">หรือ</div>
                <div className="social-btns">
                  <button
                    type="button"
                    className="btn-social"
                    onClick={handleGoogle}
                  >
                    <GoogleIcon /> Google
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>

      {/* ── Province Modal (สมาชิกใหม่จาก Social) ── */}
      {showProvinceModal && (
        <div style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(8px)",
          zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20
        }}>
          <div style={{
            background:"#ffffff",border:"1px solid rgba(247,71,53,0.15)",borderRadius:20,
            padding:32,width:"100%",maxWidth:400
          }}>
            <div style={{fontSize:"1.1rem",fontWeight:700,marginBottom:6}}>เลือกจังหวัดของคุณ</div>
            <div style={{fontSize:"0.8rem",color:"#88919e",marginBottom:20,lineHeight:1.6}}>
              เพื่อให้บริการที่ดีขึ้น กรุณาเลือกจังหวัดที่อยู่ปัจจุบัน
            </div>
            {error && <div className="error-msg">{error}</div>}
            <select
              className="form-select"
              value={socialProvince}
              onChange={(e) => setSocialProvince(e.target.value)}
              style={{marginBottom:20}}
            >
              <option value="">— เลือกจังหวัด —</option>
              {THAI_PROVINCES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <div style={{display:"flex",gap:10}}>
              <button
                onClick={() => { setShowProvinceModal(false); navigate("/home"); }}
                style={{
                  flex:1,padding:12,borderRadius:12,border:"1px solid rgba(247,71,53,0.2)",
                  background:"transparent",color:"#88919e",fontFamily:"Prompt,sans-serif",
                  fontSize:"0.85rem",cursor:"pointer"
                }}
              >
                ข้ามไปก่อน
              </button>
              <button
                onClick={handleSaveProvince}
                disabled={!socialProvince || socialProvinceLoading}
                style={{
                  flex:1,padding:12,borderRadius:12,border:"none",
                  background:"#dc3728",color:"#f5f6f8",fontFamily:"Prompt,sans-serif",
                  fontSize:"0.85rem",fontWeight:700,cursor:"pointer",opacity:(!socialProvince||socialProvinceLoading)?0.5:1
                }}
              >
                {socialProvinceLoading ? "กำลังบันทึก..." : "ยืนยัน →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
